/** Kleine Bausteine, die überall gebraucht werden. */

import type { ReactNode } from 'react'
import { ApiFehler } from '../api/klient'
import { fehlertext } from '../api/fehler'
import { STUFENTEXT, stufenklasse, type Stufe } from '../api/typen'

/**
 * Fehleranzeige.
 *
 * Wertet die Feldfehler einer 422-Antwort einzeln aus. Ein blindes Anzeigen
 * von `detail` ergäbe hier "[object Object]".
 */
export function Fehlerhinweis({ fehler }: { fehler: unknown }) {
  if (!fehler) return null

  const felder = fehler instanceof ApiFehler ? fehler.felder : []

  return (
    <div
      role="alert"
      className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3 text-stufe-3"
    >
      <p className="font-semibold">{fehlertext(fehler)}</p>
      {felder.length > 1 && (
        <ul className="mt-1 list-disc pl-5 text-base">
          {felder.map((f, i) => (
            <li key={`${f.feld}-${i}`}>
              {f.feld ? <span className="font-semibold">{f.feld}: </span> : null}
              {f.meldung}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Stufenzeichen. Immer mit Text, nie nur Farbe. */
export function Stufenzeichen({
  stufe,
  klein = false,
}: {
  stufe: Stufe | null
  klein?: boolean
}) {
  const beschriftung = stufe === null ? 'noch nicht beurteilt' : STUFENTEXT[stufe]
  return (
    <span
      className={`${stufenklasse(stufe)} ${klein ? 'px-2 text-base' : 'px-3 text-lg'}`}
      style={klein ? { minHeight: '2rem', minWidth: '2.75rem' } : undefined}
      title={beschriftung}
      aria-label={beschriftung}
    >
      {stufe ?? '–'}
    </span>
  )
}

export function Ladeanzeige({ text = 'Lädt …' }: { text?: string }) {
  return (
    <div className="flex items-center gap-3 p-6 text-slate-600" role="status">
      <span
        className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-marke-600"
        aria-hidden
      />
      {text}
    </div>
  )
}

export function Leerhinweis({ titel, text }: { titel: string; text?: ReactNode }) {
  return (
    <div className="karte border-dashed text-center text-slate-600">
      <p className="font-semibold text-slate-800">{titel}</p>
      {text ? <p className="mt-1">{text}</p> : null}
    </div>
  )
}

/** Zahl mit Beschriftung, für die Übersicht. */
export function Kennzahl({
  wert,
  beschriftung,
  betont = false,
}: {
  wert: number | string
  beschriftung: string
  betont?: boolean
}) {
  return (
    <div className={`karte ${betont ? 'border-stufe-3 bg-stufe-3-grund' : ''}`}>
      <div
        className={`text-3xl font-bold ${betont ? 'text-stufe-3' : 'text-marke-700'}`}
      >
        {wert}
      </div>
      <div className="mt-0.5 text-base text-slate-700">{beschriftung}</div>
    </div>
  )
}

export function datumDeutsch(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [jahr, monat, tag] = iso.slice(0, 10).split('-')
  return `${tag}.${monat}.${jahr}`
}

/** Ausgeschrieben mit Wochentag: „Donnerstag, 15. April 2027".
 *
 *  Für Fristen und Fälligkeiten. Ein Datum in Ziffern liest man über,
 *  ausgeschrieben fällt auf, wenn es auf einen Sonntag fällt. */
export function datumLang(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [jahr, monat, tag] = iso.slice(0, 10).split('-').map(Number)
  // Mittags: um Mitternacht kippt die Zeitzone das Datum um einen Tag.
  return new Date(jahr, monat - 1, tag, 12).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
